import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import Icon from '@material-ui/core/Icon'
import { useSnackbar } from 'notistack'
import { useApi } from '~shared/api/ApiContext'
import WebSocketContext from '~src/WebSocketContext'
import { useWidth } from '~src/new/app/providers/WidthProvider'
import { EXPERT_POINTS_NOTIFICATIONS_CATCH_UP_EVENT } from '~shared/utils/ExpertPointsNotifications'
import awardImage from '~shared/assets/img/expert-points/award.png'
import IconManual from '~src/icons/news/manual.svg'
import IconGame from '~src/icons/news/game.svg'
import IconPicTask from '~src/icons/news/pic.svg'
import IconQuiz from '~src/icons/news/quiz.svg'
import IconSurvey from '~src/icons/news/survey.svg'
import IconTask from '~src/icons/news/task.svg'
import IconChallenge from '~src/icons/news/challenge.svg'
import Rupor from '~src/icons/news/default.svg'
import cls from './expert-points-notification-manager.module.scss'

const formatPoints = points => `+${points}`

const getIds = items => items.map(({ id }) => id).filter(Boolean)

const TOAST_SNACKBAR_Z_INDEX = 2147483647
const getToastSnackbarStyle = isMobile => ({
  zIndex: TOAST_SNACKBAR_Z_INDEX,
  left: 0,
  right: 0,
  width: '100%',
  maxWidth: '100%',
  justifyContent: 'center',
  padding: isMobile ? '0 16px' : 0,
  boxSizing: 'border-box',
  transform: isMobile
    ? 'translateY(calc(-90px - env(safe-area-inset-bottom)))'
    : 'translateY(84px)',
  pointerEvents: 'none',
})

const activityIcons = {
  article: IconTask,
  quiz: IconQuiz,
  survey: IconSurvey,
  game: IconGame,
  challenge: IconChallenge,
  packshot: IconPicTask,
  planogram: IconPicTask,
  shelf: IconPicTask,
  condition3: Rupor,
}

const getActivityIcon = sourceType => activityIcons[sourceType] || IconManual

const isBrowserOffline = () => {
  return typeof navigator !== 'undefined' && navigator.onLine === false
}

const ExpertPointsToast = ({ item, onClose, isMobile }) => {
  if (!item) return null

  return (
    <div
      className={classNames(cls.toast, {
        [cls.toastDesktop]: !isMobile,
      })}
    >
      <div className={cls.toastContent}>
        <img src={awardImage} alt="" className={cls.toastAward} />
        <div className={cls.toastText}>
          <div className={cls.toastTitle}>
            <span className={cls.points}>{formatPoints(item.points)}</span>
            <span className={cls.toastLabel}>эксперт-баллов</span>
          </div>
          <div className={cls.toastDescription}>{item.description}</div>
        </div>
      </div>
      <button className={cls.toastClose} type="button" onClick={onClose} aria-label="Закрыть">
        <Icon>close</Icon>
      </button>
    </div>
  )
}

const ScrollableList = ({ children }) => {
  const listRef = useRef(null)
  const scrollbarTimerRef = useRef(null)
  const [scrollbar, setScrollbar] = useState({
    visible: false,
    active: false,
    top: 0,
    height: 100,
  })
  const childrenCount = React.Children.count(children)

  const updateScrollbar = useCallback(active => {
    const list = listRef.current
    if (!list) return

    const { scrollTop, scrollHeight, clientHeight } = list
    const visible = scrollHeight > clientHeight + 1

    if (!visible) {
      setScrollbar({ visible: false, active: false, top: 0, height: 100 })
      return
    }

    const height = Math.max((clientHeight / scrollHeight) * 100, 24)
    const top = (scrollTop / (scrollHeight - clientHeight)) * (100 - height)
    setScrollbar(prev => ({
      visible,
      active: active === undefined ? prev.active : active,
      top,
      height,
    }))
  }, [])

  const fadeScrollbar = useCallback(() => {
    if (scrollbarTimerRef.current) {
      clearTimeout(scrollbarTimerRef.current)
    }

    scrollbarTimerRef.current = setTimeout(() => {
      setScrollbar(prev => ({ ...prev, active: false }))
    }, 700)
  }, [])

  useEffect(() => {
    const frameId = requestAnimationFrame(() => {
      updateScrollbar(true)
      fadeScrollbar()
    })
    const handleResize = () => updateScrollbar()
    window.addEventListener('resize', handleResize)

    return () => {
      cancelAnimationFrame(frameId)
      if (scrollbarTimerRef.current) {
        clearTimeout(scrollbarTimerRef.current)
      }
      window.removeEventListener('resize', handleResize)
    }
  }, [childrenCount, fadeScrollbar, updateScrollbar])

  const handleListScroll = useCallback(() => {
    updateScrollbar(true)
    fadeScrollbar()
  }, [fadeScrollbar, updateScrollbar])

  return (
    <div className={cls.list}>
      <div className={cls.listScroll} ref={listRef} onScroll={handleListScroll}>
        {children}
      </div>
      {scrollbar.visible && (
        <div
          className={classNames(cls.listScrollbar, {
            [cls.listScrollbarActive]: scrollbar.active,
          })}
        >
          <div
            className={cls.listScrollbarThumb}
            style={{
              height: `${scrollbar.height}%`,
              top: `${scrollbar.top}%`,
            }}
          />
        </div>
      )}
    </div>
  )
}

const ExpertPointsModal = ({ items, onClose, isMobile }) => {
  const totalPoints = items.reduce((total, item) => total + item.points, 0)

  return (
    <div
      className={classNames(cls.modalOverlay, {
        [cls.modalOverlayDesktop]: !isMobile,
      })}
    >
      <div
        className={classNames(cls.modalSheet, {
          [cls.modalSheetSingle]: items.length === 1,
          [cls.modalSheetDesktop]: !isMobile,
        })}
      >
        <div className={cls.glow} />
        <img src={awardImage} alt="" className={cls.modalAward} />
        <button className={cls.modalClose} type="button" onClick={onClose} aria-label="Закрыть">
          <Icon>close</Icon>
        </button>

        <div className={cls.modalContent}>
          <div className={cls.modalTitle}>Начислены баллы</div>

          <div className={cls.total}>
            <div className={cls.totalPoints}>{formatPoints(totalPoints)}</div>
            <div className={cls.totalLabel}>Эксперт-баллов</div>
          </div>

          <ScrollableList>
            {items.map(item => (
              <div className={cls.listItem} key={item.id}>
                <div className={cls.itemInfo}>
                  <div className={cls.itemIcon}>
                    <img
                      className={cls.itemIconImage}
                      src={getActivityIcon(item.sourceType)}
                      alt=""
                    />
                  </div>
                  <div className={cls.itemTitle}>{item.title}</div>
                </div>
                <div className={cls.itemPoints}>{formatPoints(item.points)}</div>
              </div>
            ))}
          </ScrollableList>
        </div>

        <button className={cls.modalButton} type="button" onClick={onClose}>
          Закрыть
        </button>
      </div>
    </div>
  )
}

const useExpertPointsSnackbar = ({ isMobile, markSeen, markKnown }) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  return useCallback(
    (item, { alreadyKnown = false } = {}) => {
      if (!item || (!alreadyKnown && markKnown(item))) return

      enqueueSnackbar(item.id, {
        key: `expert-points-${item.id}`,
        anchorOrigin: {
          vertical: isMobile ? 'bottom' : 'top',
          horizontal: 'center',
        },
        className: classNames(cls.toastSnackbar, {
          [cls.toastSnackbarDesktop]: !isMobile,
        }),
        style: getToastSnackbarStyle(isMobile),
        autoHideDuration: 3000,
        preventDuplicate: true,
        onClose: () => {
          markSeen([item], 'toast')
        },
        children: key => (
          <ExpertPointsToast item={item} isMobile={isMobile} onClose={() => closeSnackbar(key)} />
        ),
      })
    },
    [closeSnackbar, enqueueSnackbar, isMobile, markKnown, markSeen],
  )
}

export const ExpertPointsNotificationManager = ({ member }) => {
  const api = useApi()
  const { socket } = useContext(WebSocketContext) || {}
  const { isMobile } = useWidth()
  const [modalItems, setModalItems] = useState([])
  const shownIdsRef = useRef(new Set())
  const catchUpTimerRef = useRef(null)

  const modalOpen = modalItems.length > 0
  const memberId = member?._id

  useEffect(() => {
    shownIdsRef.current = new Set()
    setModalItems([])
  }, [memberId])

  const markSeen = useCallback(
    (items, shownVia) => {
      const ids = getIds(items)
      if (!ids.length) return

      const { request } = api.markExpertPointsNotificationsSeen({ ids, shownVia })
      request.catch(error => {
        console.error('Failed to mark expert point notifications as seen:', error)
      })
    },
    [api],
  )

  const markKnown = useCallback(item => {
    if (!item?.id || shownIdsRef.current.has(item.id)) return true
    shownIdsRef.current.add(item.id)
    return false
  }, [])

  const showToast = useExpertPointsSnackbar({ isMobile, markSeen, markKnown })

  const fetchUnseenNotifications = useCallback(async () => {
    if (!memberId || isBrowserOffline()) return []

    const { request } = api.getUnseenExpertPointsNotifications()

    try {
      const { notifications = [] } = await request
      return notifications.filter(item => !markKnown(item))
    } catch (error) {
      console.error('Failed to load expert point notifications:', error)
      return []
    }
  }, [api, markKnown, memberId])

  const showKnownToast = useCallback(
    (item, { alreadyKnown = false } = {}) => {
      if (!item || (!alreadyKnown && markKnown(item))) return

      showToast(item, { alreadyKnown: true })
    },
    [markKnown, showToast],
  )

  const showUnseenToasts = useCallback(
    unseen => {
      unseen.forEach(item => showKnownToast(item, { alreadyKnown: true }))
    },
    [showKnownToast],
  )

  const catchUpUnseenNotifications = useCallback(async () => {
    const unseen = await fetchUnseenNotifications()
    showUnseenToasts(unseen)
  }, [fetchUnseenNotifications, showUnseenToasts])

  const scheduleCatchUpUnseenNotifications = useCallback(() => {
    if (catchUpTimerRef.current) {
      clearTimeout(catchUpTimerRef.current)
    }

    catchUpTimerRef.current = setTimeout(() => {
      catchUpUnseenNotifications()
    }, 800)
  }, [catchUpUnseenNotifications])

  useEffect(() => {
    return () => {
      if (catchUpTimerRef.current) {
        clearTimeout(catchUpTimerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!socket) return

    const onExpertPointsAccrued = item => showKnownToast(item)

    const onSocketReconnect = () => {
      scheduleCatchUpUnseenNotifications()
    }

    socket.on('expertPointsAccrued', onExpertPointsAccrued)
    socket.io?.on('reconnect', onSocketReconnect)

    return () => {
      socket.off('expertPointsAccrued', onExpertPointsAccrued)
      socket.io?.off('reconnect', onSocketReconnect)
    }
  }, [scheduleCatchUpUnseenNotifications, showKnownToast, socket])

  useEffect(() => {
    if (!memberId) return

    let cancelled = false

    const loadInitialUnseenNotifications = async () => {
      const unseen = await fetchUnseenNotifications()
      if (cancelled) return
      if (unseen.length) setModalItems(unseen)
    }

    loadInitialUnseenNotifications()

    return () => {
      cancelled = true
    }
  }, [fetchUnseenNotifications, memberId])

  useEffect(() => {
    if (!memberId) return

    window.addEventListener('online', scheduleCatchUpUnseenNotifications)
    window.addEventListener(
      EXPERT_POINTS_NOTIFICATIONS_CATCH_UP_EVENT,
      scheduleCatchUpUnseenNotifications,
    )

    return () => {
      window.removeEventListener('online', scheduleCatchUpUnseenNotifications)
      window.removeEventListener(
        EXPERT_POINTS_NOTIFICATIONS_CATCH_UP_EVENT,
        scheduleCatchUpUnseenNotifications,
      )
    }
  }, [memberId, scheduleCatchUpUnseenNotifications])

  const handleModalClose = useCallback(() => {
    const closedItems = modalItems
    setModalItems([])
    markSeen(closedItems, 'modal')
  }, [markSeen, modalItems])

  return modalOpen ? (
    <ExpertPointsModal items={modalItems} isMobile={isMobile} onClose={handleModalClose} />
  ) : null
}
